// @ts-nocheck

import useFeatureFlags from 'hooks/useFeatureFlags'
import { filter, forEach, keys, omit } from 'lodash'
import PropTypes from 'prop-types'
import { useEffect } from 'react'
import { connect } from 'react-redux'
import { useInvalidateMedicationQueries } from 'screens/Residents/screens/ResidentEmarV2Screens/hooks/useInvalidateMedicationQueries'
import Actions from '../../../../actions'
import { getObjectDiff } from '../../../../common/utils'
import Operations from '../../../../operations'
import { FormFooter, SectionHeader } from '../../../generic'
import AddMedDetailsStep from '../../../modals/AddEmarMedicationModal/steps/AddMedDetailsStep'
import AddMedInstructionsStep from '../../../modals/AddEmarMedicationModal/steps/AddMedInstructionsStep'
import Form from '../..'

type Medication = {
  emarRoundsDetails: { altDose: string; dose: string }[]
}

const InnerForm = ({
  eMARStockEnabled,
  medication,
  fields,
  resident,
  onConfirm,
  onDelete,
  onCancel,
  readOnly,
  isSubmitting,
  setFormField,
  setFormState,
  formState,
  showToast,
  loadListItem,
  remove,
  submitForm,
  errors,
  testId
}: unknown & {
  medication: Medication
  testId?: string
}) => {
  const flags = useFeatureFlags({ flagNames: ['emar_v2_chart'] })?.data
  const emarV2ChartEnabled = flags?.emar_v2_chart?.enabled ?? false
  const invalidateMedicationQueries = useInvalidateMedicationQueries()
  const medicationUrl = emarV2ChartEnabled
    ? `/emar/medication-schedule-patterns/legacy/emar-resident-medications/${medication.id}`
    : `list/emar-resident-medications/${medication.id}`

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const med = medication.medication

    if (medication.emarRoundsDetails.some((round) => round.altDose)) {
      setFormState({ key: 'hasAltDose', value: true })
    }

    forEach(keys(med), (key) => {
      if (key === 'controlledDrug') {
        if (med.controlledDrug) {
          ;[
            { key: 'secondSignatureRequired', value: true },
            { key: 'homeRemedy', value: false },
            { key: 'overTheCounter', value: false },
            { key: 'blisterPack', value: false }
          ].forEach((field) => {
            setFormState({ key: `${field.key}PrePopped`, value: true })
          })
          setFormState({ key: `controlledDrugPrePopped`, value: !!fields[key] })
        }
        setFormState({ key: `controlledDrugInitial`, value: med[key] }) // Stores the original nhs value for controlled drug
      } else if (key === 'unit') {
        setFormState({ key: `quantityTypePrePopped`, value: !!med[key] })
        setFormState({ key: `freeTextDose`, value: !med[key] })
        setFormState({ key: `defaultQuantityType`, value: med[key] })
      } else {
        setFormState({ key: `${key}PrePopped`, value: !!med[key] })
      }
    })
    return () => {
      remove({
        onConfirm: onCancel
      }) // clear form state on unmount
    }
  }, [])

  if (!fields || Object.keys(fields).length === 0) return null

  return (
    <div
      style={styles.wrapper}
      data-testid={
        testId ? `${testId}-edit-medication-form` : 'edit-medication-form'
      }
    >
      <AddMedInstructionsStep
        editState
        formId={FORM_ID}
        resident={resident}
        fields={fields}
        errors={errors}
        formState={formState}
        setFormState={setFormState}
        testId={testId}
      />
      <SectionHeader
        greyLine
        lineStyles={styles.sectionTitleLine}
        title="Details"
        titleStyles={styles.sectionTitle}
        data-testid={
          testId ? `${testId}-details-section-header` : 'details-section-header'
        }
      />
      <AddMedDetailsStep
        eMARStockEnabled={eMARStockEnabled}
        editState
        errors={errors}
        fields={fields}
        formId={FORM_ID}
        formState={formState}
        resident={resident}
        setFormField={setFormField}
        setFormState={setFormState}
        testId={testId}
      />
      {!medication.deleted && (
        <FormFooter
          containerStyles={{ width: '100%' }}
          isSubmitting={isSubmitting}
          onConfirm={() => {
            submitForm({
              url: medicationUrl,
              data: fields,
              initial: medication,
              onComplete: () => {
                // The eMAR V2 screens read the medication through react-query,
                // which holds a query fresh for five minutes. Without this the
                // information page we return to re-renders its pre-save copy
                // of the medication until the page is reloaded (ROST-2596).
                invalidateMedicationQueries()
                showToast({ title: 'Updated a Medication' })
                loadListItem(
                  'emar-resident-medications',
                  medication.id,
                  undefined,
                  emarV2ChartEnabled ? medicationUrl : undefined
                )
                onConfirm()
              }
            })
          }}
          confirmLabel={readOnly ? 'Edit' : 'Save'}
          onDelete={onDelete}
          deleteLabel="Discontinue"
          onCancel={() => {
            remove({
              onConfirm: onCancel
            })
          }}
          cancelLabel="Cancel"
          testId={testId}
        />
      )}
    </div>
  )
}

export const EditMedicationForm = (props) => {
  if (!props.fields || !props.medication) return null
  return <InnerForm {...props} />
}

const FORM_ID = 'editMedication'

const styles = {
  wrapper: {
    paddingBottom: 38,
    paddingTop: 24
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 400,
    lineHeight: '30px',
    marginTop: 32,
    marginBottom: 3
  },
  sectionTitleLine: {
    marginBottom: 16
  }
}

EditMedicationForm.propTypes = {
  medication: PropTypes.object
}

const mapStateToProps = (state, props) => ({
  eMARStockEnabled: state.global.home.features?.eMARStock?.enabled
})

const mapDispatchToProps = (dispatch) => ({
  addForm: (data) => dispatch(Actions.addForm(data)),
  loadListItem: (listId, itemId, query, url) =>
    dispatch(Operations.loadListItem(listId, itemId, query, url)),
  submitForm: ({ initial, data, onComplete, url }) => {
    const changes = getObjectDiff(data, initial)

    const body = omit(changes, [
      'emarRoundsDetails',
      'emarRoundTimings',
      'medication',
      'hasEndDate',
      'control',
      'repeat',
      'customFreq'
    ])

    if (initial.endDate && !data.hasEndDate) {
      body.endDate = null
    }

    dispatch(
      Operations.postItem({
        url,
        formId: FORM_ID,
        method: 'PUT',
        json: body,
        onComplete
      })
    )
  }
})

const wrappedForm = connect(
  mapStateToProps,
  mapDispatchToProps
)(EditMedicationForm)

export default Form(wrappedForm, FORM_ID)
